import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/base/VideoCard';
import { useSeo } from '@/hooks/useSeo';
import { videos, categories } from '@/mocks/videos';

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = (params.get('q') || '').trim();
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => {
    if (!query) return [...videos].sort((a, b) => b.views - a.views);
    const q = query.toLowerCase();
    return videos.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.categoryName.toLowerCase().includes(q) ||
        v.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [query]);

  useSeo({
    title: query ? `搜索「${query}」 - 啪啪视频` : '搜索视频 - 啪啪视频',
    description: query
      ? `在啪啪视频中搜索「${query}」，找到相关的高清视频内容并在线播放，发现更多精彩影片。`
      : '在啪啪视频中按关键词搜索高清视频内容，快速找到你想看的影片。',
    keywords: `啪啪视频,视频搜索,${query || '高清视频'},在线播放`,
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <Layout>
      <div className="pt-16">
        <div className="px-4 md:px-6 py-8 md:py-10 border-b border-background-200/70 bg-background-100/50">
          <h1 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">
            {query ? '搜索结果' : '发现视频'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {query ? (
              <>
                关键词：<span className="text-accent-500 font-medium">「{query}」</span> · 共找到{' '}
                <span className="text-foreground-900 font-semibold">{results.length}</span> 个结果
              </>
            ) : (
              <>输入关键词，按标题、分类或标签搜索你想看的高清视频</>
            )}
          </p>
          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-xl">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片标题或标签，如：科幻、短片"
                className="w-full h-11 pl-9 pr-3 text-sm rounded-md bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="px-4 md:px-6 py-8">
          {results.length > 0 ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {results.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              {!query && (
                <div className="mt-10">
                  <p className="text-sm text-foreground-500 mb-3">按分类浏览</p>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="px-3 py-2 rounded-md border border-background-200/70 text-sm text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center">
              <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">无匹配视频</h2>
              <p className="mt-2 text-sm text-foreground-600">
                没有找到与「{query}」相关的内容，试试其他关键词吧。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => { setKeyword(''); navigate('/search'); }}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
                >
                  <i className="ri-refresh-line text-base" />
                  重置搜索
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-200/70 text-foreground-800 text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-colors"
                >
                  <i className="ri-home-4-line text-base" />
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}